import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { vehicleService } from '@/services/vehicleService'
import { employeesService } from '@/services/employeesService'
import type { VehicleVm, VehicleTypeOption } from '@/api/types/vehicle.types'
import { showError, showSuccess } from '@/lib/swal'

function toDateInputValue(d?: string): string {
  if (!d) return ''
  try {
    const date = new Date(d)
    return date.toISOString().slice(0, 10)
  } catch {
    return String(d).slice(0, 10)
  }
}

function toIsoOrUndefined(d?: string): string | undefined {
  if (!d) return undefined
  const value = String(d).trim()
  if (!value) return undefined
  // Input type="date" gives "YYYY-MM-DD"; Swagger sample uses ISO datetime.
  const isoCandidate = value.includes('T') ? value : `${value}T00:00:00.000Z`
  const dt = new Date(isoCandidate)
  if (Number.isNaN(dt.getTime())) return undefined
  return dt.toISOString()
}

export const VehicleEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const tv = (key: string) => t(`empVehicle.${key}`)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(() => {
    navigate(listEmpId ? `/vehicles?empId=${listEmpId}` : '/vehicles')
  }, [navigate, listEmpId])

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([])

  const [form, setForm] = useState({
    empId: 0,
    vehicleCompanyName: '',
    modelYear: '' as string | number,
    vehicleColor: '',
    vehicleNumber: '',
    vehicleTypeId: 0,
    parkingAllotted: false,
    parkingAllotNumber: '' as string | number,
    registrationStartDate: '',
    registrationEndDate: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing && listEmpId) {
      setForm((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
    }
  }, [isEditing, listEmpId])

  useEffect(() => {
    vehicleService.getVehicleTypes().then((data) => setVehicleTypes(data ?? [])).catch(() => setVehicleTypes([]))
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await vehicleService.getById(Number(id))
        const raw = data as any
        const isParkingAllot = raw?.isParkingAllot === true || raw?.isParkingAllot === 1
        const parkingAllotted =
          isParkingAllot || String(raw?.isParkingAllot ?? raw?.parkingAllotted ?? '').toUpperCase() === 'YES'
        setForm({
          empId: data.empId ?? 0,
          vehicleCompanyName: data.vehicleCompanyName ?? '',
          modelYear: data.modelYear ?? data.model ?? 0,
          vehicleColor: data.modelColor ?? data.vehicleColor ?? '',
          vehicleNumber: data.carNumberPlate ?? data.carNumber ?? data.vehicleNumber ?? '',
          vehicleTypeId: data.carTypeId ?? data.vehicleTypeId ?? 0,
          parkingAllotted,
          parkingAllotNumber: data.parkingAllotNumber ?? '',
          registrationStartDate: toDateInputValue(data.carRegistrationStartDate ?? data.registrationStartDate),
          registrationEndDate: toDateInputValue(data.carRegistrationEndDate ?? data.registrationEndDate),
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? tv('loadFailed'))
        backToList()
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, backToList])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label =
          fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? t('empVehicle.employeeFallback', { id: e.id ?? 0 })
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees, t]
  )

  const vehicleTypeOptions = useMemo(
    () =>
      vehicleTypes.map((vt) => {
        const name =
          (vt as any).lookupNameEn ??
          (vt as any).lookupNameAr ??
          (vt as any).name ??
          t('empVehicle.typeFallback', { id: vt.id ?? 0 })
        return { value: String(vt.id ?? 0), label: name }
      }),
    [vehicleTypes, t]
  )

  const employeeSelectLocked = isEditing || listEmpId > 0
  const displayEmpCode = useMemo(() => {
    const emp = employees.find((e) => e.id === form.empId)
    return emp?.empCode?.trim() || '—'
  }, [employees, form.empId])

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(tv('branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(tv('employeeRequired'))
      return
    }
    if (!form.vehicleCompanyName.trim()) {
      toast.error(tv('companyNameRequired'))
      return
    }
    const modelYear = typeof form.modelYear === 'number' ? form.modelYear : parseInt(String(form.modelYear), 10)
    if (!Number.isFinite(modelYear) || modelYear < 1900 || modelYear > 2100) {
      toast.error(tv('modelYearInvalid'))
      return
    }
    if (!form.vehicleColor.trim()) {
      toast.error(tv('colorRequired'))
      return
    }
    if (!form.vehicleNumber.trim()) {
      toast.error(tv('vehicleNumberRequired'))
      return
    }
    const allotNum = typeof form.parkingAllotNumber === 'number' ? form.parkingAllotNumber : parseInt(String(form.parkingAllotNumber), 10)
    if (!Number.isFinite(allotNum) || allotNum < 0) {
      toast.error(tv('parkingAllotInvalid'))
      return
    }

    const carRegistrationStartDate = toIsoOrUndefined(form.registrationStartDate)
    const carRegistrationEndDate = toIsoOrUndefined(form.registrationEndDate)

    const payload: VehicleVm = {
      // Swagger sample sends id: 0 for new
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Backend (Swagger) field names
      vehicleCompanyName: form.vehicleCompanyName.trim(),
      modelId: 0,
      modelYear,
      modelColor: form.vehicleColor.trim(),
      carNumberPlate: form.vehicleNumber.trim(),
      carTypeId: form.vehicleTypeId,
      isParkingAllot: form.parkingAllotted ? 1 : 0,
      parkingAllotNumber: allotNum,

      carRegistrationStartDate,
      carRegistrationEndDate,
      branchId,

      // Frontend/backward compatibility field names
      vehicleColor: form.vehicleColor.trim(),
      vehicleNumber: form.vehicleNumber.trim(),
      carNumber: form.vehicleNumber.trim(),
      vehicleTypeId: form.vehicleTypeId,
      parkingAllotted: form.parkingAllotted,
      registrationStartDate: form.registrationStartDate || undefined,
      registrationEndDate: form.registrationEndDate || undefined,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await vehicleService.update(payload)
        await showSuccess(tv('updatedTitle'), tv('updatedMessage'))
      } else {
        await vehicleService.save(payload)
        await showSuccess(tv('savedTitle'), tv('savedMessage'))
      }
      backToList()
    } catch (e: any) {
      await showError(tv('saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? tv('saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={tv('formTitle')} description={tv('formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{tv('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={tv('formTitle')}
        description={isEditing ? tv('formDescriptionEdit') : tv('formDescriptionAdd')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{tv('cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emp-code-display">{tv('labelEmpCode')}</Label>
              <Input id="emp-code-display" readOnly value={displayEmpCode} className="bg-muted/50" aria-readonly />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelEmployee')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: tv('placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => {
                  if (employeeSelectLocked) return
                  handleChange({ empId: Number(v) || 0 })
                }}
                placeholder={tv('placeholderSelectEmployee')}
                searchPlaceholder={tv('searchEmployeePlaceholder')}
                emptyMessage={tv('emptyEmployee')}
                disabled={employeeSelectLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelVehicleCompanyName')}
              </Label>
              <Input
                value={form.vehicleCompanyName}
                onChange={(e) => handleChange({ vehicleCompanyName: e.target.value })}
                placeholder={tv('placeholderCompanyName')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelModelYear')}
              </Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.modelYear === '' ? '' : form.modelYear}
                onChange={(e) => handleChange({ modelYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder={tv('placeholderModelYear')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelVehicleColor')}
              </Label>
              <Input
                value={form.vehicleColor}
                onChange={(e) => handleChange({ vehicleColor: e.target.value })}
                placeholder={tv('placeholderVehicleColor')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelVehicleNumber')}
              </Label>
              <Input
                value={form.vehicleNumber}
                onChange={(e) => handleChange({ vehicleNumber: e.target.value })}
                placeholder={tv('placeholderVehicleNumber')}
              />
            </div>
            <div className="space-y-2">
              <Label>{tv('labelVehicleType')}</Label>
              <SearchableSelect
                options={[{ value: '0', label: tv('placeholderSelectVehicleType') }, ...vehicleTypeOptions]}
                value={form.vehicleTypeId ? String(form.vehicleTypeId) : '0'}
                onValueChange={(v) => handleChange({ vehicleTypeId: Number(v) || 0 })}
                placeholder={tv('placeholderVehicleType')}
                searchPlaceholder={tv('searchVehicleTypePlaceholder')}
                emptyMessage={tv('emptyVehicleType')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelParkingAllotted')}
              </Label>
              <Select
                value={form.parkingAllotted ? 'YES' : 'NO'}
                onValueChange={(v) => handleChange({ parkingAllotted: v === 'YES' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="YES">{t('common.yes')}</SelectItem>
                  <SelectItem value="NO">{t('common.no')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {tv('requiredMark')} {tv('labelParkingAllotNumber')}
              </Label>
              <Input
                type="number"
                min={0}
                value={form.parkingAllotNumber === '' ? '' : form.parkingAllotNumber}
                onChange={(e) => handleChange({ parkingAllotNumber: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder={tv('placeholderParkingAllot')}
              />
            </div>
            <div className="space-y-2">
              <Label>{tv('labelRegistrationStart')}</Label>
              <Input
                type="date"
                value={form.registrationStartDate}
                onChange={(e) => handleChange({ registrationStartDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{tv('labelRegistrationEnd')}</Label>
              <Input
                type="date"
                value={form.registrationEndDate}
                onChange={(e) => handleChange({ registrationEndDate: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => backToList()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {tv('return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? t('common.update') : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
